import { useLatestOrders } from '../../hooks/useLatestOrders';
import { Config } from '../../config';
import { useFormatting, useTranslation } from '../../i18n/i18n';
import { fetchResourceMap, optionalResource, PreloadedData, Res, useApi, useCms } from '../../state/api';
import { ProductContainer } from '../../state/product';
import { useSession } from '../../state/session';
import { UiContainer } from '../../state/ui';
import { AccountPageNavigation } from '../AccountPageNavigation/AccountPageNavigation';
import { ActiveOrders } from '../ActiveOrders/ActiveOrders';
import { CheckmarkList } from '../CheckmarkList/CheckmarkList';
import { ContentCard } from '../ContentCard/ContentCard';
import { ButtonLink } from '../Form/Button';
import { Icon, gift } from '../Icon/Icon';
import { InfoCardBoard } from '../InfoCardBoard/InfoCardBoard';
import { Main } from '../Main/Main';
import { OutstandingItems } from '../OutstandingItems/OutstandingItems';
import { Routing } from '../Routes';
import { VerticalSpacer } from '../VerticalSpacer/VerticalSpacer';
import { CmsComponent, isRenderable } from '../cms/CmsComponent';
import { CmsMostBought } from '../cms/CmsMostBought/CmsMostBought';
import { renderTemplate } from '../utils';
import './MyPages.css';
import { getSelectedSpecialPriceProducts } from './SpecialPricesPage';

/* the two blocks this page lets the cms own, top and bottom; a piece renders only where it is asked for by name */
const TOP_PIECE = 'my-pages-top';
const BOTTOM_PIECE = 'my-pages-bottom';

/** enough of the latest orders to be useful, with the rest a click away */
const ORDERS_SHOWN = 5;

export const MyPagesPage = (): JSX.Element => {
	const { user } = useSession();
	const { data: bonusBalance } = useApi(Res.bonusBalance, {});
	const { data: orderStats } = useApi(Res.orderStats, {});
	const { data: activeOrders } = useLatestOrders();
	const { data: bonusRegisterData } = useApi(user && !user.bonus ? Res.bonusRegisterData : null, {});
	const { data: outstanding } = useApi(Res.outstandingItems, {});
	const { data: topContent } = useCms(Res.cmsComponent, { slug: TOP_PIECE });
	const { data: bottomContent } = useCms(Res.cmsComponent, { slug: BOTTOM_PIECE });

	const { specialPricesIndex } = ProductContainer.useContainer();
	const selectedSpecialProducts = getSelectedSpecialPriceProducts(specialPricesIndex);
	const t = useTranslation().myPages;
	const routes = useTranslation().routes;
	const bonusT = useTranslation().bonusPage;
	const genericT = useTranslation().generic;
	const { priceWithVat } = UiContainer.useContainer();
	const { asPrice } = useFormatting();
	const visibleActiveOrders = activeOrders?.slice(0, ORDERS_SHOWN);
	const hasMoreOrders = (activeOrders?.length ?? 0) > ORDERS_SHOWN;

	const totalOrderVolume = orderStats
		? priceWithVat
			? orderStats.totalExVat + orderStats.vat
			: orderStats.totalExVat
		: 0;
	const bonusRegistrationHeading =
		bonusRegisterData?.bonusAmount && bonusRegisterData.bonusAmount > 0
			? renderTemplate(bonusT.cashInOnPreviousPurchase, {
					// two decimals, and not rounded to nothing: 0,10 was showing as 0
					amount: asPrice(bonusRegisterData.bonusAmount, 2, false),
				})
			: bonusT.becomeBonusCustomerToday;

	return (
		<Main>
			<div id="my-pages-page">
				<AccountPageNavigation />
				<VerticalSpacer spacing="28px" direction="bottom" />
				{/* an emptied piece is published as [], which renders nothing - and must not leave its spacing behind */}
				{isRenderable(topContent) ? (
					<>
						<CmsComponent structure={topContent} />
						<VerticalSpacer spacing="44px" direction="bottom" />
					</>
				) : null}

				{visibleActiveOrders?.length ? (
					<>
						<div className="my-pages-section-heading">
							<div>
								<h2>{t.latestOrders}</h2>
							</div>
						</div>
						<VerticalSpacer spacing="24px" mobileSpacing="20px" direction="bottom" />
						<ActiveOrders orders={visibleActiveOrders} />
						{hasMoreOrders ? (
							<>
								<VerticalSpacer spacing="24px" direction="bottom" />
								<div className="my-pages-view-all">
									<ButtonLink linkTo={routes.yourOrders}>{genericT.viewAll}</ButtonLink>
								</div>
							</>
						) : null}
						<VerticalSpacer spacing="64px" direction="bottom" />
					</>
				) : null}

				{outstanding?.items?.length ? (
					<>
						<div className="my-pages-section-heading">
							<div>
								<h2>{t.outstandingItems}</h2>
							</div>
						</div>
						<VerticalSpacer spacing="24px" mobileSpacing="20px" direction="bottom" />
						<OutstandingItems />
						<VerticalSpacer spacing="64px" direction="bottom" />
					</>
				) : null}

				{user && !user.bonus ? (
					<>
						<div className="bonus-register-cards-grid">
							<ContentCard className="register">
								<div className="my-pages-bonus-copy">
									<h2>{bonusT.registerAsBonusCustomer}</h2>
									<p>{bonusT.registerAsBonusCustomerCopy}</p>
									<CheckmarkList className="black compact">
										<li>{bonusT.registerChecklistA}</li>
										<li>{bonusT.registerChecklistB}</li>
										<li>{bonusT.registerChecklistC}</li>
									</CheckmarkList>
								</div>
							</ContentCard>
							<div className="bonus-box">
								<ContentCard className="bonus-wallet">
									<Icon shape={gift} size={100} />
									<h2>{bonusRegistrationHeading}</h2>
								</ContentCard>
								<ButtonLink linkTo={routes.bonus}>{bonusT.becomeBonusCustomer}</ButtonLink>
							</div>
						</div>
						<VerticalSpacer spacing="64px" direction="bottom" />
					</>
				) : null}

				<InfoCardBoard
					bonusBalance={bonusBalance?.notActivatedBonusAmount || 0}
					specialPricesProductCount={selectedSpecialProducts.length}
					totalOrderVolume={totalOrderVolume}
				/>
				<VerticalSpacer spacing="64px" direction="bottom" />

				<CmsMostBought />

				{isRenderable(bottomContent) ? (
					<>
						<VerticalSpacer spacing="104px" mobileSpacing="96px" direction="bottom" />
						<CmsComponent structure={bottomContent} />
					</>
				) : null}
			</div>
		</Main>
	);
};

export const myPagesRouting: Routing = {
	path(urls) {
		return urls.myPages;
	},
	component: MyPagesPage,
	requiresAuth: true,
	preload: async (config: Config): Promise<PreloadedData> => {
		const pieces = await Promise.all(
			[TOP_PIECE, BOTTOM_PIECE].map((slug) =>
				optionalResource(fetchResourceMap(config, Res.cmsComponent, { slug }, config.cmsContent))
			)
		);
		return Object.assign({}, ...pieces);
	},
};
